import React from 'react';
import { Box, FormHelperText, IconButton, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { get, isEmpty } from 'lodash';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAppInfo } from 'utils/hooks/useAppInfo';
import {
  COMPACT_MODE_FORM_ELEMENT_FONT_SIZE,
  COMPACT_MODE_FORM_ELEMENT_HEIGHT,
  COMPACT_MODE_FORM_ELEMENT_LINE_HEIGHT,
} from 'constants/constants';

const InputForm = ({
  control,
  name,
  label,
  required,
  type = 'text',
  disabled = false,
  multiline,
  rows,
  inputProps,
  InputProps,
  defaultValue = '',
  placeholder,
  sx = {},
  customOnChange,
  autoFocus = false,
}: any) => {
  const { t } = useTranslation();
  const {
    field: { ref, value, onChange, onBlur, ...props },
  } = useController({
    name,
    control,
    defaultValue,
  });

  const { compactMode } = useAppInfo();

  const handleTrimOnBlur = (event: any) => {
    const trimmedValue = event.target.value.trim();
    onChange(trimmedValue);
    onBlur();
  };

  const maxLength = get(inputProps, 'maxLength', 0);
  const {
    watch,
    formState: { errors },
  } = useFormContext();
  const watchedItem = maxLength > 0 ? watch(name, '') : '';
  const countFromMaxLength =
    maxLength > 0 && watchedItem && watchedItem.length > 0
      ? ` (${watchedItem ? watchedItem.length : 0} / ${maxLength})`
      : '';

  let isError = false;
  let errorMessage = '';
  if (!isEmpty(errors) && get(errors, name) !== undefined) {
    isError = true;
    errorMessage = get(errors, `${name}.message`, '') as string;
  }
  return (
    <div style={{ position: 'relative' }}>
      {!disabled && value !== undefined && value !== null && value !== '' ? (
        <Box
          sx={{
            position: 'absolute',
            right: (theme) => theme.spacing(0.2),
            top: (theme) => (compactMode ? theme.spacing(1) : theme.spacing(1)),
            zIndex: 69,
          }}
        >
          <Tooltip title={t('Form:clearValue')}>
            <IconButton
              onClick={() => {
                onChange('');
              }}
              size={compactMode ? 'small' : 'medium'}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ) : null}
      <TextField
        {...props}
        value={value || value === 0 ? value : ''}
        onChange={(e) => {
          onChange(e);
          if (customOnChange) {
            customOnChange(e);
          }
        }}
        onBlur={handleTrimOnBlur}
        type={type}
        label={`${label}${countFromMaxLength}`}
        fullWidth={true}
        error={isError}
        margin="dense"
        size="small"
        autoFocus={autoFocus}
        InputLabelProps={{
          className: required ? 'required-label' : '',
          required: required || false,
          sx: compactMode
            ? {
                fontSize: COMPACT_MODE_FORM_ELEMENT_FONT_SIZE,
                lineHeight: COMPACT_MODE_FORM_ELEMENT_LINE_HEIGHT,
              }
            : {},
        }}
        inputRef={ref}
        placeholder={
          placeholder
            ? placeholder
            : /* compactMode ? formatLabelForRequired(label, required) : */ ''
        }
        disabled={disabled}
        multiline={multiline}
        rows={rows}
        inputProps={{
          ...inputProps,
        }}
        InputProps={{
          ...InputProps,
          sx: compactMode
            ? {
                ...(multiline ? {} : { height: COMPACT_MODE_FORM_ELEMENT_HEIGHT }),
                fontSize: COMPACT_MODE_FORM_ELEMENT_FONT_SIZE,
                ...InputProps?.sx,
              }
            : { ...InputProps?.sx },
        }}
        sx={{}}
      />
      {isError && (
        <FormHelperText error={true} id={`error-text-${name}`} data-pw={`error-text-${name}`}>
          {errorMessage}
        </FormHelperText>
      )}
    </div>
  );
};

export default InputForm;
